import clsx from 'clsx';
import { CHANNEL } from '../../../constants/messageTemplateKeys';
import { Card, CardTitle } from '../../../../../shared/ui/card';
import { TextField } from '../../../../../shared/ui/textfield';
import { Select } from '../../../../../shared/ui/select/Select';
import { MultiSelect } from '../../../../../shared/ui/select/MultiSelect';
import { TemplateStatusBadge } from '../../template-status-badge/TemplateStatusBadge';
import { LanguageFlag } from '../../language-flag';
import { TemplateUpdatedLabel } from '../../template-updated-label/TemplateUpdatedLabel';
import { TemplateIdLabel } from '../../template-id-label/TemplateIdLabel';
import { formatDateTimeLabel } from '../../../../../shared/lib/formatDateTimeLabel';
import styles from './BasicInformationCard.module.css';
import { useBasicInformationForm } from './lib/useBasicInformationForm';
import { SubjectsField } from './components/SubjectsField';
import { SendConditionsField } from './components/SendConditionsField';
import { TypeSelector } from './components/TypeSelector';
import { LanguageSelector } from './components/LanguageSelector';

export function BasicInformationCard({
  className = '',
  channel = CHANNEL.EMAIL,
  saveAttempt = 0,
  revealNonce = 0,
  onFormChange,
  onTouchedChange,
  initialValues,
  requireTemplateName = false,
  hasTriedSave = false,
  currentStatus = null,
  modified = null,
  lastEditedBy = null,
  templateId = null,
  isEditMode = false,
  isAdmin = false,
  ownTenantId = null,
}) {
  const {
    templateName,
    type,
    language,
    languageOptions,
    showLanguageSelector,
    showLanguageFlag,
    onLanguageChange,
    subjects,
    selectedCampaignIds,
    setSelectedCampaignIds,
    selectedTagIds,
    setSelectedTagIds,
    triggerId,
    setTriggerId,
    sendConditions,
    onChangeSendConditionAt,
    onRemoveSendConditionAt,
    onAddSendCondition,
    canAddNextSendCondition,
    onSendConditionBlur,
    sendConditionRowErrors,
    campaignWidthProbeRef,
    tagsMenuWidthPx,
    isEmail,
    isMarketing,
    errors,
    templateNameRequiredError,
    templateNameInvalid,
    canAddNextSubject,
    subjectErrors,
    campaignOptions,
    tagOptions,
    triggerOptions,
    tenantOptions,
    selectAllTenantValue,
    selectedTenantIds,
    onChangeTenants,
    tenantRequiredError,
    onSetSubjectAt,
    onRemoveSubjectAt,
    onAddSubject,
    onTypeChange,
    setTemplateName,
  } = useBasicInformationForm({
    channel,
    saveAttempt,
    revealNonce,
    onFormChange,
    onTouchedChange,
    initialValues,
    requireTemplateName,
    hasTriedSave,
    templateId,
    isAdmin,
    ownTenantId,
    isEditMode,
    currentStatus,
  });

  // The tenant multiselect is an admin-only, create-only control.
  const showTenantSelect = isAdmin && !isEditMode;

  const hasSendConditionErrors = (sendConditionRowErrors ?? []).some(
    (rowError) => rowError.sendCondition || rowError.day || rowError.dayInvalid,
  );

  const modifiedDate = formatDateTimeLabel(modified);
  const editorName = lastEditedBy
    ? `${lastEditedBy.first_name} ${lastEditedBy.last_name}`.trim()
    : null;

  return (
    <Card className={clsx(className)} aria-label="Basic Information">
      <div className={clsx(styles.cardHeaderGroup)}>
        <TemplateIdLabel templateId={templateId} />

        <div className={clsx(styles.cardHeader)}>
          <CardTitle className={clsx(styles.cardHeaderTitle)}>Basic Information</CardTitle>
          {showLanguageFlag ? <LanguageFlag language={language} /> : null}
          {currentStatus && <TemplateStatusBadge status={currentStatus} />}
          {modifiedDate && (
            <TemplateUpdatedLabel>
              Last updated {modifiedDate}
              {editorName ? ` by ${editorName}` : ''}
            </TemplateUpdatedLabel>
          )}
        </div>
      </div>
      <div className={clsx(styles.body)}>
        {showLanguageSelector ? (
          <LanguageSelector
            language={language}
            options={languageOptions}
            onLanguageChange={onLanguageChange}
          />
        ) : null}

        <div className={clsx(styles.fieldGroup)}>
          <label htmlFor="basic-template-name" className={clsx(styles.label)}>
            Template Name
          </label>
          <TextField
            id="basic-template-name"
            value={templateName}
            onChange={setTemplateName}
            placeholder="e.g., Welcome Email - Pre-Charge Off"
            ariaLabel="Template Name"
            variant="form"
            invalid={templateNameInvalid}
          />
          {errors.templateNameMaxError ? (
            <div className={clsx(styles.errorText)} data-field-error>
              {errors.templateNameMaxError}
            </div>
          ) : null}
          {templateNameRequiredError ? (
            <div className={clsx(styles.errorText)} data-field-error>
              {templateNameRequiredError}
            </div>
          ) : null}
        </div>

        {isEmail ? (
          <SubjectsField
            subjects={subjects}
            onSetSubjectAt={onSetSubjectAt}
            onRemoveSubjectAt={onRemoveSubjectAt}
            onAddSubject={onAddSubject}
            canAddNextSubject={canAddNextSubject}
            subjectErrors={subjectErrors}
          />
        ) : null}

        {showTenantSelect ? (
          <div className={clsx(styles.fieldGroup)}>
            <label htmlFor="basic-tenants" className={clsx(styles.label)}>
              Select Tenant
            </label>
            <MultiSelect
              inputId="basic-tenants"
              values={selectedTenantIds}
              options={tenantOptions}
              onChange={onChangeTenants}
              selectAllValue={selectAllTenantValue}
              ariaLabel="Select Tenant"
              placeholder="Select"
              variant="form"
              menuWidth="field"
              filledBg="surface"
              invalid={Boolean(tenantRequiredError)}
              maxMenuRows={5}
              showOptionCheckboxes
            />
            {tenantRequiredError ? (
              <div className={clsx(styles.errorText)} data-field-error>
                {tenantRequiredError}
              </div>
            ) : null}
          </div>
        ) : null}

        <TypeSelector type={type} onTypeChange={onTypeChange} />

        <div className={clsx(styles.fieldGroup)}>
          <label htmlFor="basic-campaign" className={clsx(styles.label)}>
            Campaign
            {!isMarketing ? <span className={clsx(styles.optional)}> (optional)</span> : null}
          </label>
          <div ref={campaignWidthProbeRef} className={clsx(styles.campaignWidthProbe)}>
            <MultiSelect
              inputId="basic-campaign"
              values={selectedCampaignIds}
              options={campaignOptions}
              onChange={(next) => setSelectedCampaignIds((next ?? []).map(String))}
              ariaLabel="Campaign"
              placeholder="Select"
              variant="form"
              menuWidth="field"
              filledBg="surface"
              invalid={Boolean(errors.campaignError)}
              maxMenuRows={5}
              showOptionCheckboxes
            />
          </div>
          {errors.campaignError ? (
            <div className={clsx(styles.errorText)} data-field-error>
              {errors.campaignError}
            </div>
          ) : null}
        </div>

        {!isMarketing ? (
          <>
            <div className={clsx(styles.fieldGroup)}>
              <label htmlFor="basic-trigger" className={clsx(styles.label)}>
                Trigger Condition
              </label>
              <Select
                inputId="basic-trigger"
                value={triggerId}
                options={triggerOptions}
                onChange={(val) => setTriggerId(String(val))}
                ariaLabel="Trigger Condition"
                variant="form"
                menuWidth="field"
                invalid={Boolean(errors.triggerError)}
                maxMenuRows={5}
              />
              {errors.triggerError ? (
                <div className={clsx(styles.errorText)} data-field-error>
                  {errors.triggerError}
                </div>
              ) : null}
            </div>

            <SendConditionsField
              sendConditions={sendConditions}
              onChangeSendConditionAt={onChangeSendConditionAt}
              onRemoveSendConditionAt={onRemoveSendConditionAt}
              onAddSendCondition={onAddSendCondition}
              onFieldBlur={onSendConditionBlur}
              canAddNext={canAddNextSendCondition && !hasSendConditionErrors}
              rowErrors={sendConditionRowErrors}
            />
          </>
        ) : null}

        <div className={clsx(styles.fieldGroup)}>
          <label htmlFor="basic-tags" className={clsx(styles.label)}>
            Tags <span className={clsx(styles.optional)}>(optional)</span>
          </label>
          <MultiSelect
            inputId="basic-tags"
            values={selectedTagIds}
            options={tagOptions}
            onChange={(next) => setSelectedTagIds((next ?? []).map(String))}
            ariaLabel="Tags"
            placeholder="Select"
            variant="form"
            menuWidth="field"
            filledBg="surface"
            maxMenuRows={5}
            showOptionCheckboxes
            menuWidthPx={tagsMenuWidthPx ?? undefined}
          />
        </div>
      </div>
    </Card>
  );
}
